"use client"

import { useActionState, useRef, useState } from "react"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import { signup } from "@lib/data/customer"
import { Mail, Lock, User, Phone } from "lucide-react"
import ReCAPTCHA from "react-google-recaptcha"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
}

const Register = ({ setCurrentView }: Props) => {
  const [formState, formAction] = useActionState(signup, null)
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (password !== confirmPassword) {
      e.preventDefault()
      setConfirmError("Passwords do not match")
      return
    }
    setConfirmError(null)

    if (!recaptchaToken) {
      e.preventDefault()
      setRecaptchaError("Please verify you are not a robot")
      return
    }
    setRecaptchaError(null)
  }

  return (
    <div data-testid="register-page">
      <h2 className="text-2xl font-black text-center text-gray-900 mb-1 uppercase tracking-wide">
        Create Account
      </h2>
      <p className="text-center text-sm text-gray-500 mb-8">
        Join the UK Soccer Shop squad
      </p>

      <form className="w-full" action={formAction} onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* First Name + Last Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                First Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  name="first_name"
                  type="text"
                  placeholder="First Name"
                  required
                  autoComplete="given-name"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                  data-testid="first-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                Last Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  name="last_name"
                  type="text"
                  placeholder="Last Name"
                  required
                  autoComplete="family-name"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                  data-testid="last-name-input"
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              E-Mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="email"
                type="email"
                placeholder="E-Mail"
                required
                autoComplete="email"
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="email-input"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Phone
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="phone"
                type="tel"
                placeholder="Phone (optional)"
                autoComplete="tel"
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="phone-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="password"
                type="password"
                placeholder="Password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="password-input"
              />
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="confirm_password"
                type="password"
                placeholder="Confirm Password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
        </div>

        <ErrorMessage
          error={confirmError ?? formState?.error}
          data-testid="register-error"
          className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
        />

        <label className="flex items-center gap-2 text-sm text-gray-600 mt-4 mb-6 cursor-pointer">
          <input
            name="accepts_marketing"
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300"
            data-testid="accepts-marketing-checkbox"
          />
          Keep me up to date on news and exclusive offers
        </label>

        <div className="mb-6">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
            onChange={(token) => {
              setRecaptchaToken(token)
              setRecaptchaError(null)
            }}
            onExpired={() => setRecaptchaToken(null)}
          />
          <ErrorMessage
            error={recaptchaError}
            data-testid="recaptcha-error"
            className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          />
        </div>

        <SubmitButton
          data-testid="register-button"
          className="w-full py-3.5 bg-[#1a1a2e] text-white font-bold rounded-lg hover:bg-[#2a2a3e] transition-colors flex items-center justify-center gap-2"
        >
          Create Account
          <span aria-hidden="true">&rarr;</span>
        </SubmitButton>
      </form>

      {/* OR divider */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <p className="text-center text-sm text-gray-500 mb-4">Sign Up With...</p>

      {/* Social buttons */}
      <div className="grid grid-cols-1">
        <button
          type="button"
          className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Google
        </button>
      </div>
    </div>
  )
}

export default Register
